/**
 * When a SQL Server text literal is written `N'…'` — the ONE policy both
 * filter emitters share: the renderer's `filterWhereBuilder.ts` (Visual Query
 * Editor chips, Data Profile, loose-FK join filters) and Table Explorer's
 * `electron/table-explorer/queryBuilder/queryUtils.ts`, with the JSON filter
 * emitter (`jsonFilterSql.ts`) under both.
 *
 * A plain `'…'` literal is varchar in the database's code page, and SQL Server
 * converts it through that code page before it compares anything: a character
 * the code page lacks (`Ł` under a Latin1 collation, any CJK text, an emoji)
 * becomes `?` or a look-alike, so `city = 'Łódź'` on an nvarchar column finds
 * no row. `N'…'` is nvarchar and keeps every character.
 *
 * - An nchar / nvarchar / ntext / sysname column: always `N'…'`.
 * - A char / varchar / text column: never (but see below). An nvarchar
 *   literal outranks the column's type, so SQL Server converts the COLUMN
 *   instead, which can turn an index seek into a scan — and the column cannot
 *   hold a character its code page lacks anyway.
 * - Any other or unknown type — a column the schema does not resolve (a CTE
 *   column, an expression), `JSON_VALUE` (returns nvarchar), an alias type,
 *   `sql_variant`: `N'…'` only when the value holds a character outside ASCII.
 *   Every SQL Server code page spells ASCII the same way, so an ASCII value
 *   keeps the plain literal and compares the same either way.
 *
 * WHY A VALUE OUTSIDE THE BMP ALSO TAKES A COLLATE
 * A value with an emoji or another character outside the Basic Multilingual
 * Plane (a surrogate pair in UTF-16) is written
 * `N'…' COLLATE Latin1_General_100_CI_AS_SC`, on every column type. The usual
 * database collations (`SQL_Latin1_General_CP1_CI_AS`, `Latin1_General_CI_AS`)
 * give a surrogate pair no weight, so it compares as if it were absent:
 * `N'😀' = N'😁'` and `N'ab😀' = N'ab'` are true, and `contains 😀`
 * (`LIKE N'%😀%'`) matches every non-null row (measured on SQL Server 2025).
 * An explicit collation on the literal outranks the column's, and the
 * supplementary-character-aware `_SC` one compares the characters —
 * case-insensitive and accent-sensitive like those defaults (SQL Server 2012+,
 * the oldest version we support). A char / varchar column takes it too: under
 * a code-page collation it cannot hold the character, and a plain literal
 * would become `??` and match the rows holding `??`; compared as nvarchar it
 * matches none (and on a `_UTF8` varchar column, which can hold it, the right
 * rows). The price is an index scan instead of a seek, for such a value only.
 *
 * A JSON PATH OR KEY NAME (`JSON_VALUE(col, N'$."東京"')`, `OPENJSON`'s
 * `[key] = N'東京'`) is written `N'…'` when it holds a character outside
 * ASCII — a plain `'$."東京"'` becomes `'$."??"'` and finds nothing — and
 * never with a COLLATE: SQL Server matches JSON keys by code point (`[key]` is
 * `Latin1_General_BIN2`), which a case-insensitive collation would loosen.
 *
 * The other engines take no prefix: PostgreSQL, MySQL and SQLite literals are
 * in the connection's UTF-8, and Oracle's in the database character set —
 * AL32UTF8 by default since 12.2, where a plain literal holds every character
 * (an NVARCHAR2 column in a single-byte database would need `N'…'` too; not
 * handled). Their default collations compare characters outside the BMP.
 * MySQL / MariaDB's `utf8mb4_general_ci` and `utf8mb4_unicode_ci` weigh all of
 * them the same (`😀` matches `😁`, never a row without one) — not handled:
 * MySQL 8's default `utf8mb4_0900_ai_ci` compares them, and MariaDB has no
 * collation every supported version shares to switch to.
 */

const NATIONAL_TYPES: ReadonlySet<string> = new Set(['nchar', 'nvarchar', 'ntext', 'sysname']);
const NON_UNICODE_TYPES: ReadonlySet<string> = new Set(['char', 'varchar', 'text']);

/** The collation a value outside the BMP is compared under (see the header). */
export const SQL_SERVER_SUPPLEMENTARY_COLLATION = 'Latin1_General_100_CI_AS_SC';

/** Whether `value` holds a character outside the Basic Multilingual Plane. */
function hasSupplementaryCharacter(value: string): boolean {
  return /[\u{10000}-\u{10FFFF}]/u.test(value);
}

/** `nvarchar(40)` / `NVARCHAR(max)` → `nvarchar`. */
function baseTypeOf(dataType: string): string {
  return dataType.toLowerCase().replace(/\(.*?\)/g, '').trim();
}

/** Whether SQL Server writes `value` as `N'…'` against a `dataType` column (see the header). */
export function sqlServerTextIsNational(value: string, dataType?: string): boolean {
  const base = dataType ? baseTypeOf(dataType) : '';
  if (NATIONAL_TYPES.has(base) || hasSupplementaryCharacter(value)) return true;
  if (NON_UNICODE_TYPES.has(base)) return false;
  return /\P{ASCII}/u.test(value);
}

/**
 * `quoted` — the `'…'` literal an escaper wrote for `value` — with the `N`
 * prefix when the policy above asks for it, and the `COLLATE` of a value
 * outside the BMP. Anything else (a number, `NULL`, a literal that already has
 * its prefix) is returned unchanged.
 */
export function withSqlServerNationalPrefix(quoted: string, value: unknown, dataType?: string): string {
  if (typeof value !== 'string' || !quoted.startsWith("'") || !sqlServerTextIsNational(value, dataType)) return quoted;
  return hasSupplementaryCharacter(value) ? `N${quoted} COLLATE ${SQL_SERVER_SUPPLEMENTARY_COLLATION}` : `N${quoted}`;
}

/** A JSON path or key name's quoted literal `quoted`, with `N` when `text` holds a character outside ASCII (see the header). */
export function withSqlServerNationalPathPrefix(quoted: string, text: string): string {
  return /\P{ASCII}/u.test(text) ? `N${quoted}` : quoted;
}
